/**
 * orgShiftUtils — Helpers para jornada/turno por setor e cargo
 *
 * Regra de prioridade:
 *   1. Cargo (role.shiftMinutes / role.shiftsPerDay)
 *   2. Setor (dept.shiftMinutes / dept.shiftsPerDay)
 *   3. Padrão global: shiftMinutes=480, shiftsPerDay=1
 *
 *   dailyCapacity = shiftMinutes × shiftsPerDay
 */

/** Retorna os min/turno configurados no setor */
export function getDeptShiftMinutes(organization: any, departmentId: string): number {
  if (!organization?.departments) return 480;
  const dept = organization.departments.find((d: any) => d.id === departmentId);
  return (dept as any)?.shiftMinutes ?? 480;
}

/** Retorna os turnos/dia configurados no setor */
export function getDeptShiftsPerDay(organization: any, departmentId: string): number {
  if (!organization?.departments) return 1;
  const dept = organization.departments.find((d: any) => d.id === departmentId);
  return (dept as any)?.shiftsPerDay ?? 1;
}

/** Retorna a capacidade diária total do setor (min/turno × turnos/dia) */
export function getDeptDailyCapacity(organization: any, departmentId: string): number {
  if (!organization?.departments) return 480;
  const dept = organization.departments.find((d: any) => d.id === departmentId);
  if (!dept) return 480;
  const shift = (dept as any).shiftMinutes ?? 480;
  const turns = (dept as any).shiftsPerDay ?? 1;
  return shift * turns;
}

/**
 * Retorna o min/turno de um cargo pelo roleId.
 * Prioridade: role.shiftMinutes → dept.shiftMinutes → 480
 */
export function getRoleShiftMinutes(organization: any, roleId: string): number {
  if (!organization?.roles) return 480;
  const role = organization.roles.find((r: any) => r.id === roleId);
  if (!role) return 480;
  if ((role as any).shiftMinutes) return (role as any).shiftMinutes;
  return getDeptShiftMinutes(organization, role.departmentId);
}

/**
 * Retorna a capacidade diária de um cargo pelo roleId.
 * Prioridade campos: role → dept → padrão
 */
export function getRoleDailyCapacity(organization: any, roleId: string): number {
  if (!organization?.roles) return 480;
  const role = organization.roles.find((r: any) => r.id === roleId);
  if (!role) return 480;
  const shift = (role as any).shiftMinutes || getDeptShiftMinutes(organization, role.departmentId);
  const turns = (role as any).shiftsPerDay || getDeptShiftsPerDay(organization, role.departmentId);
  return shift * turns;
}

/** Retorna a capacidade diária de um cargo pelo NOME do cargo */
export function getRoleDailyCapacityByName(organization: any, roleName: string): number {
  if (!organization?.roles || !roleName) return 480;
  const role = organization.roles.find((r: any) =>
    (r.name || '').toLowerCase() === roleName.toLowerCase()
  );
  if (!role) return 480;
  return getRoleDailyCapacity(organization, role.id);
}

/** Retorna os min/turno de um cargo pelo NOME do cargo */
export function getRoleShiftMinutesByName(organization: any, roleName: string): number {
  if (!organization?.roles || !roleName) return 480;
  const role = organization.roles.find((r: any) =>
    (r.name || '').toLowerCase() === roleName.toLowerCase()
  );
  if (!role) return 480;
  return getRoleShiftMinutes(organization, role.id);
}
